import React from 'react';
import { GlobalStyles as MuiGlobalStyles, useTheme } from '@mui/material';
import { typographyTokens } from '../theme/typography-tokens';
import useAutoHideScrollbars from './useAutoHideScrollbars';

const GlobalStyles: React.FC = () => {
  // useTheme from @mui/material already unwraps the THEME_ID-scoped theme;
  // unwrapping again yields undefined and silently drops the token-derived rules.
  const theme = useTheme();
  useAutoHideScrollbars();

  return (
    <MuiGlobalStyles
      styles={{
        // Box model + font rendering
        '*': {
          boxSizing: 'border-box',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          lineHeight: 'normal',
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          transition: 'scrollbar-color 0.3s ease',
        },
        '.is-scrolling': {
          scrollbarColor: `${theme.palette.tokens.surfaceLight} transparent`,
        },
        // Margin / padding / border reset for all common HTML elements
        'html, body, div, span, applet, object, iframe, h1, h2, h3, h4, h5, h6, hr, p, blockquote, pre, a, abbr, acronym, address, big, cite, code, del, dfn, em, img, ins, kbd, q, s, samp, small, strike, strong, sub, sup, tt, var, b, u, i, center, dl, dt, dd, ol, ul, li, fieldset, form, label, legend, table, caption, tbody, tfoot, thead, tr, th, td, article, aside, canvas, details, embed, figure, figcaption, footer, header, hgroup, menu, nav, output, ruby, section, summary, time, mark, audio, video, button':
          {
            margin: 0,
            padding: 0,
            border: 0,
            fontSize: '100%',
            verticalAlign: 'baseline',
          },
        // Form elements inherit font
        'button, input, select, textarea': {
          fontFamily: 'inherit',
        },
        a: {
          color: 'inherit',
          textDecoration: 'inherit',
        },
        // Disabled state for native disabled elements only.
        // aria-disabled is intentionally excluded: pounceV2 components that use
        // aria-disabled (e.g. ChipDeleteButton) need pointer-events intact so
        // they can intercept clicks and call stopPropagation.
        ':disabled, :disabled:focus, :disabled:hover': {
          opacity: 0.3,
          pointerEvents: 'none',
          cursor: 'default',
        },
        // Correct nested disabled items so opacity isn't applied multiple times
        ':disabled *, :disabled:focus *, :disabled:hover *': {
          opacity: 1,
        },
        // HTML5 display-role reset
        'article, aside, details, figcaption, figure, footer, header, hgroup, menu, nav, section': {
          display: 'block',
        },
        html: {
          color: theme.palette.tokens.content.text.neutral.base,
          fontFamily: typographyTokens.fontFamilies.sans,
        },
        body: {
          lineHeight: 1,
        },
        'ol, ul': {
          listStyle: 'none',
        },
        'blockquote, q': {
          quotes: 'none',
        },
        'blockquote:before, blockquote:after, q:before, q:after': {
          content: "''",
        },
        table: {
          borderCollapse: 'collapse',
          borderSpacing: 0,
        },
        svg: {
          fill: 'currentColor',
        },
        'input, textarea': {
          outline: 'none',
        },
        "input[type='number']": {
          WebkitAppearance: 'textfield',
          MozAppearance: 'textfield',
          appearance: 'textfield',
        },
        "input[type='number']::-webkit-inner-spin-button, input[type='number']::-webkit-outer-spin-button":
          {
            WebkitAppearance: 'none',
          },
      }}
    />
  );
};

export default GlobalStyles;
